// AuthGuard — calls GET /api/auth/check on mount, redirects unauthenticated
// users to /login?next=<current-path>. While authenticated it listens for
// cinedb:unauthorized events dispatched by apiFetch on any unexpected 401,
// triggering an immediate redirect to /login.

import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

import { apiFetch } from "../api/client";

type GuardState = "checking" | "ok" | "denied";

export function AuthGuard({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<GuardState>("checking");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    apiFetch("/api/auth/check")
      .then((r) => {
        if (!cancelled) setState(r.ok ? "ok" : "denied");
      })
      .catch(() => {
        if (!cancelled) setState("denied");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Redirect to /login whenever any API call returns 401 unexpectedly
  // (e.g. server restarted and wiped sessions while the app was open).
  useEffect(() => {
    if (state !== "ok") return;
    function onUnauthorized() {
      navigate("/login", { replace: true });
    }
    window.addEventListener("cinedb:unauthorized", onUnauthorized);
    return () => window.removeEventListener("cinedb:unauthorized", onUnauthorized);
  }, [state, navigate]);

  if (state === "checking") {
    // Intentional blank — the guard runs fast on a warm server; a spinner
    // would flash distractingly.
    return null;
  }

  if (state === "denied") {
    const next = `${location.pathname}${location.search}`;
    const target = next === "/" ? "/login" : `/login?next=${encodeURIComponent(next)}`;
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
